import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useAdminAvailabilityEvents, useAdminHotFacilities } from '../../hooks/useAdminApi';

export function AdminAvailabilityPage() {
  const eventsQuery = useAdminAvailabilityEvents();
  const hotQuery = useAdminHotFacilities();

  // Backend already returns events bucketed by day
  const days = useMemo(() => {
    return eventsQuery.data?.events ?? [];
  }, [eventsQuery.data]);

  const last7 = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return days.filter(d => d.day >= cutoffStr);
  }, [days]);

  if (eventsQuery.isLoading || hotQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#8b949e] animate-spin" />
      </div>
    );
  }

  const today = days.length > 0 ? days[days.length - 1] : null;
  const todayAvailable = today?.became_available ?? 0;
  const todayUnavailable = today?.became_unavailable ?? 0;

  const total7dAvailable = last7.reduce((s, d) => s + d.became_available, 0);
  const total7dUnavailable = last7.reduce((s, d) => s + d.became_unavailable, 0);
  const total7d = total7dAvailable + total7dUnavailable;
  const cancellationRate = total7d > 0 ? (total7dUnavailable / total7d) * 100 : 0;

  const hotFacilities = hotQuery.data?.facilities ?? [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-white">Availability</h1>
        <p className="text-[13px] text-[#8b949e]">Event trends and hottest facilities</p>
      </div>

      {/* 3-across stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">TODAY&apos;S AVAILABLE</div>
          <div className="text-2xl font-bold text-emerald-500">{todayAvailable}</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">became available events</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">TODAY&apos;S UNAVAILABLE</div>
          <div className="text-2xl font-bold text-red-500">{todayUnavailable}</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">became unavailable events</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">CANCELLATION RATE 7D</div>
          <div className="text-2xl font-bold text-amber-500">{cancellationRate.toFixed(1)}%</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">{total7dUnavailable} / {total7d} events</div>
        </div>
      </div>

      {/* Events/day chart + summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">EVENTS / DAY (7D)</div>
          {last7.length === 0 ? (
            <div className="text-[13px] text-[#8b949e] py-4 text-center">No event data</div>
          ) : (
            <>
              <div className="flex items-end gap-1 h-[120px]">
                {(() => {
                  const max = Math.max(...last7.map(d => d.became_available + d.became_unavailable), 1);
                  return last7.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col justify-end gap-px">
                      {d.became_available > 0 && (
                        <div
                          className="bg-emerald-500 rounded-t"
                          style={{ height: `${(d.became_available / max) * 100}%` }}
                        />
                      )}
                      {d.became_unavailable > 0 && (
                        <div
                          className="bg-red-500 rounded-t"
                          style={{ height: `${(d.became_unavailable / max) * 100}%` }}
                        />
                      )}
                    </div>
                  ));
                })()}
              </div>
              <div className="flex justify-between mt-2">
                {last7.map((d, i) => (
                  <div key={i} className="flex-1 text-center text-[9px] text-[#8b949e]">
                    {d.day.slice(5)}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">7-DAY SUMMARY</div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-[#8b949e]">Total events this week</span>
              <span className="text-[13px] font-medium text-white">{total7d}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-[#8b949e]">Daily average</span>
              <span className="text-[13px] font-medium text-white">
                {last7.length > 0 ? (total7d / last7.length).toFixed(0) : '0'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-[#8b949e]">Available events</span>
              <span className="text-[13px] font-medium text-emerald-500">{total7dAvailable}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-[#8b949e]">Unavailable events</span>
              <span className="text-[13px] font-medium text-red-500">{total7dUnavailable}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hot facilities */}
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
        <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">MOST ACTIVE FACILITIES (7D)</div>
        <div className="space-y-2">
          {hotFacilities.length === 0 ? (
            <div className="text-[13px] text-[#8b949e] py-4 text-center">No facility data</div>
          ) : (
            hotFacilities.slice(0, 10).map((fac, i) => (
              <div
                key={fac.facilityId}
                className="flex items-center p-2.5 bg-[#1c2129] rounded-md"
              >
                <div className="text-amber-500 font-bold text-[15px] w-8 shrink-0">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-white truncate">
                    {fac.facilityName ?? 'Unknown'}
                  </div>
                </div>
                <div className="text-[13px] text-emerald-500 font-medium mr-3">
                  {fac.eventCount} events
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
